import { useEffect, useRef, useState } from 'react'

import { formatServerDate } from 'commons/utils/date'
import { convertBooleanApiValue } from 'commons/utils/string'
import { getUser } from 'commons/utils/user'
import { FooterOrderResume, Modal, Page } from 'components'
import { TypesValidations } from 'constants/enums'
import statusRascunho from 'constants/statusRascunho'
import { URL_CONST } from 'constants/urls'
import ConfigOrder from 'container/SalesOrder/components/ConfigOrder'
import { ModalExtraMaterials } from 'container/SalesOrder/components/ModalExtraMaterials'
import OrderOverviewModal from 'container/SalesOrder/components/OrderOverviewModal'
import type { SimulateDraft } from 'container/SalesOrder/interfaces'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as DraftOV from 'services/draft'
import type { RequestBodyConfirmDraft, RequestBodySimulateDraft } from 'services/draft/interfaces'
import { getOrderReport } from 'services/manage_ov'
import {
  clearSimulationAlteredMaterials,
  setPersistFormData,
  setRequestSimulate,
  setSimulateOrder,
} from 'store/SalesOrderContext/salesOrderSlice'
import { setSalesOrderInitialTab, updateOrderStep } from 'store/StructuralContext/structuralSlice'
import { useDispatch, useSelector } from 'store/store'

import SimulateOrder from './components/SimulateOrder'

const SalesOrderSimulate = () => {
  const { orderNumber } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { simulateOrder, draftBody, persistFormData } = useSelector((store) => store.salesOrder)
  const isMounted = useRef(false)

  const [modalEditConfirm, setModalEditConfirm] = useState(false)
  const [openOrderOverview, setOpenOrderOverview] = useState<boolean>(false)
  const [openExtraMaterials, setOpenExtraMaterials] = useState<boolean>(false)
  const [isLoadingGenerateOV, setIsLoadingGenerateOV] = useState(false)
  const [loadingProceed, setIsLoadingProceed] = useState(false)
  const [loadingReport, setLoadingReport] = useState(false)

  const { orderStep } = useSelector((store) => store.structure)

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (orderStep === 0) {
      navigate(`${URL_CONST.MANAGE_ORDER}/${orderNumber}`)
    }
  }, [orderStep])

  const methods = useForm({
    defaultValues: persistFormData,
  })
  const { getValues } = methods

  const handleEditConfirm = () => {
    dispatch(setPersistFormData({ ...getValues() }))
    dispatch(updateOrderStep(0))
    navigate(`${URL_CONST.ORDERS}/${orderNumber}`)
  }

  const handleReturnToEdit = () => {
    dispatch(updateOrderStep(0))
    navigate(`${URL_CONST.ORDERS}/${orderNumber}`)
    setOpenExtraMaterials(false)
  }

  const clearAlteredStatus = () => {
    dispatch(clearSimulationAlteredMaterials())
  }

  const handleEditAction = () => {
    clearAlteredStatus()
    setModalEditConfirm(true)
  }

  const continueExtraMaterials = () => {
    setOpenExtraMaterials(false)
    setOpenOrderOverview(true)
  }

  const returnOrderOverview = () => {
    setOpenOrderOverview(false)
  }

  const hasOV = () => !!draftBody?.totalizador?.qt_tota_item_soli
  const isFairShareOffice = convertBooleanApiValue(draftBody.fl_utlz_fair_shar)

  const handleSimulateProceed = async () => {
    setIsLoadingProceed(true)
    const params: RequestBodySimulateDraft = {
      cd_matr_anls_pedi_rasc: [],
      cd_usua_alte: getUser(),
      ds_item_pedi_rasc: [],
      dt_alte_antr: formatServerDate(),
      nr_pedi_rasc: orderNumber ?? '',
      nr_simu: simulateOrder?.nr_simu || '',
    }

    const data: SimulateDraft = await DraftOV.postSimulateDraft(params)

    if (!data) {
      setIsLoadingProceed(false)
      return
    }
    dispatch(setPersistFormData({ ...getValues() }))
    dispatch(setSimulateOrder(data))
    setIsLoadingProceed(false)

    const noExtraMaterialsModal =
      data?.materiais?.every((material) => material.stat === statusRascunho.ANSWERED) ||
      isFairShareOffice

    if (noExtraMaterialsModal) {
      setOpenOrderOverview(true)
    } else {
      setOpenExtraMaterials(true)
    }
  }

  const handleGenerateOVs = async () => {
    if (!simulateOrder) return
    setIsLoadingGenerateOV(true)

    if (draftBody.fl_pedi_antp === TypesValidations.YES) {
      clearAlteredStatus()
      setOpenOrderOverview(false)
      setIsLoadingGenerateOV(false)
      navigate(`${URL_CONST.MANAGE_ORDER}/${orderNumber}`)
      toast.success('Pedido de Pré-venda solicitado com sucesso')
    } else {
      const params: RequestBodyConfirmDraft = {
        cd_usua_alte: getUser(),
        dt_alte_antr: formatServerDate(),
        nr_simu: simulateOrder.nr_simu,
      }

      try {
        await DraftOV.postConfirmDraft(params)

        if (simulateOrder.resumo.vl_tota_orde_gera === 0) {
          toast.info('Nenhum material foi solicitado')
          dispatch(setSalesOrderInitialTab(1))
        } else {
          toast.success('Materiais solicitados com sucesso')
          dispatch(setSalesOrderInitialTab(0))
        }

        dispatch(setRequestSimulate({} as RequestBodySimulateDraft))
        navigate(`${URL_CONST.MANAGE_ORDER}/${orderNumber}`)
      } catch {
        toast.error('Erro ao solicitar materiais')
      } finally {
        clearAlteredStatus()

        if (isMounted.current) {
          setOpenOrderOverview(false)
        }

        setIsLoadingGenerateOV(false)
      }
    }
  }

  const downloadReport = async (orderID: string) => {
    try {
      setLoadingReport(true)
      const report = await getOrderReport(orderID)
      // TODO: Replace with utils/downlodFile({file, fileName, fileType})
      const url = window.URL.createObjectURL(new Blob([report]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `relatorio_pedido_${orderID}.xlsx`)
      link.click()
      toast.success('Relatório baixado com sucesso!')
    } catch {
      toast.error('Não foi possível baixar o relatório. Tente novamente mais tarde.')
    } finally {
      setLoadingReport(false)
    }
  }

  return (
    <>
      {orderStep === 1 && (
        <FormProvider {...methods}>
          <Page
            fullwidth
            renderHeaderComponent={
              draftBody?.nr_pedi_rasc && <ConfigOrder hasOVs={hasOV()} step={1} />
            }
          >
            {simulateOrder && (
              <>
                <SimulateOrder data={simulateOrder} />
                <FooterOrderResume
                  isLoadingReport={loadingReport}
                  isProceedLoading={loadingProceed}
                  onDownloadReport={() => downloadReport(simulateOrder.nr_pedi_rasc)}
                  onEdit={handleEditAction}
                  onProceed={handleSimulateProceed}
                  totalOfOrder={simulateOrder.vl_tota_atnd}
                  totalOfPairs={simulateOrder.qt_tota_atnd_conv}
                  type="proceed"
                />
              </>
            )}
            <Modal
              buttonAction={() => {
                setModalEditConfirm(false)
                handleEditConfirm()
              }}
              buttonText="Editar materiais"
              closeAction={() => setModalEditConfirm(false)}
              description="Ao voltar para a configuração do pedido, as possíveis alterações de volume realizadas não serão salvas. Deseja prosseguir mesmo assim?"
              open={modalEditConfirm}
              returnAction={() => setModalEditConfirm(false)}
              returnText="Não"
              setOpen={setModalEditConfirm}
              showReturn
              title="Editar materiais"
            />
            <OrderOverviewModal
              loadingConfirm={isLoadingGenerateOV}
              onConfirm={handleGenerateOVs}
              onReturn={returnOrderOverview}
              open={openOrderOverview}
              setOpen={setOpenOrderOverview}
            />
            {openExtraMaterials && (
              <ModalExtraMaterials
                onConfirm={continueExtraMaterials}
                onReturn={handleReturnToEdit}
                open={openExtraMaterials}
                setOpen={setOpenExtraMaterials}
              />
            )}
          </Page>
        </FormProvider>
      )}
    </>
  )
}

export default SalesOrderSimulate
